"use client"

import { useState, useMemo, useEffect, useRef } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Calendar, MapPin, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react"
import { Button } from "~/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "~/components/ui/dropdown-menu"
import EventsFilterBar from "~/components/events/EventsFilterBar"
import EventSaveButton from "~/components/events/EventSaveButton"
import { discoverEmptyState } from "~/lib/discover-filters"
import EventShareButton from "~/components/events/EventShareButton"
import EventCalendarButton from "~/components/events/EventCalendarButton"
import { resolveImageUrl } from "~/lib/images"
import { summarizeLocation } from "~/lib/location"
import { getEventDate, withTimeZone } from "~/lib/event-dates"
import { env } from "~/env"
import { scoreEventsByPrompt } from "~/lib/event-prompt-score"
import { logSearch, logSearchClick } from "~/lib/activity-tracking"
import { avatarGradient } from "~/lib/avatar"
import { priceChipWithDeposit as priceChipWithDepositFromTicketTypes } from "~/lib/event-price"
import type { Event } from "~/app/discovery/page"

const ITEMS_PER_PAGE = 24

// Path Collective visual system
const FONT_UI = "font-[family-name:var(--font-rounded)]"
const FONT_HEADING = "font-[family-name:var(--font-sora)]"
const FONT_BODY = "font-[family-name:var(--font-nunito)]"
const PAGINATION_BUTTON = `${FONT_UI} rounded-full font-bold text-[13.5px] bg-[#FFFDF8] border-[#D9CDB9] text-[#5E594F] hover:text-[#2C2D2B] hover:border-[#8A8475] hover:bg-[#FFFDF8]`

const SORT_OPTIONS = ["Soonest", "Latest", "Most popular"] as const
type SortOption = (typeof SORT_OPTIONS)[number]

// Eyebrow color rotation — terra / teal / gold, like the visual system's cards.
const EYEBROW_COLORS = ["text-[#DE866D]", "text-[#75A096]", "text-[#D99E37]"]

// ── Card display helpers ──────────────────────────────────────────────────────

/** "THU · JUN" + "18" for the cover's date chip, in the event's timezone. */
function dateChipParts(event: Event): { m: string; d: string } | null {
  const date = getEventDate(event)
  if (!date) return null
  try {
    const opts = (o: Intl.DateTimeFormatOptions) => withTimeZone(o, event.timezone)
    const weekday = date.toLocaleDateString("en-US", opts({ weekday: "short" }))
    const month = date.toLocaleDateString("en-US", opts({ month: "short" }))
    const day = date.toLocaleDateString("en-US", opts({ day: "numeric" }))
    return { m: `${weekday} · ${month}`.toUpperCase(), d: day }
  } catch {
    return null
  }
}

/** "6:00 PM → 7:15 PM" (or just the start) in the event's timezone. */
function timeRangeLabel(event: Event): string | null {
  const start = getEventDate(event)
  if (!start) return null
  const end = event.end_datetime ? new Date(event.end_datetime) : null
  try {
    const opts = withTimeZone({ hour: "numeric", minute: "2-digit" }, event.timezone)
    const startLabel = start.toLocaleTimeString("en-US", opts)
    if (!end || Number.isNaN(end.getTime())) return startLabel
    return `${startLabel} → ${end.toLocaleTimeString("en-US", opts)}`
  } catch {
    return null
  }
}

/**
 * Price chip text from ticket types: "Free", "$45", "From $20", "Donation".
 * Shared with the public facilitator profile's event cards (#1019), so both
 * surfaces word the same tiers the same way. #1440 FR4 — the full price with
 * a "deposit $X" note when the cheapest paid tier is a deposit ticket.
 */
function priceChip(event: Event): { label: string; depositNote: string | null } | null {
  return priceChipWithDepositFromTicketTypes(event.ticket_types)
}

export default function EventsDiscoverClient({
  events,
  showOnlySaved = false,
  accessToken,
  adminMode = false,
}: {
  events: Event[]
  showOnlySaved?: boolean
  accessToken?: string
  adminMode?: boolean
}) {
  const router = useRouter()
  const [filters, setFilters] = useState<any>({})
  const [savedEventIds, setSavedEventIds] = useState<string[]>([])
  const [page, setPage] = useState(0)
  const [sort, setSort] = useState<SortOption>("Soonest")
  const [filterResetSignal, setFilterResetSignal] = useState(0)
  const emptyState = discoverEmptyState({ totalEvents: events.length, filters, showOnlySaved })

  useEffect(() => {
    if (!accessToken) { setSavedEventIds([]); return }
    const fetchSaved = async () => {
      try {
        const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/saved`, {
          method: "get",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        })
        if (res.ok) {
          const data = (await res.json()) as { id: string }[]
          setSavedEventIds(data.map((e) => e.id))
        }
      } catch (error) {
        console.error("Failed to fetch saved events:", error)
      }
    }
    void fetchSaved()
  }, [accessToken])

  const handleSavedToggle = async (eventId: string, saved: boolean) => {
    if (!accessToken) return
    try {
      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/events/${eventId}/save`, {
        method: saved ? "post" : "delete",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      })
      if (res.ok) {
        setSavedEventIds((prev) =>
          saved ? [...prev, eventId] : prev.filter((id) => id !== eventId)
        )
      }
    } catch (error) {
      console.error("Failed to toggle save:", error)
    }
  }

  const filteredAndSorted = useMemo(() => {
    let result = showOnlySaved
      ? events.filter((e) => savedEventIds.includes(e.id))
      : [...events]

    const now = new Date()

    // ── SEARCH PROMPT ─────────────────────────────────────────────────────────
    // Single unified AI search. Score by tokens against host profile_tags +
    // categories + title + facilitator focus + descriptions. Drop score-0
    // events and remember scores for the sort tiebreak below.
    let scoreById: Map<string, number> | null = null
    if (filters.smartPrompt?.trim()) {
      const scored = scoreEventsByPrompt(result, filters.smartPrompt)
      scoreById = new Map(scored.map((s) => [s.event.id, s.score]))
      result = scored.map((s) => s.event)
    }

    // ── DATE FILTER ───────────────────────────────────────────────────────────
    if (filters.date) {
      result = result.filter((ev) => {
        const d = getEventDate(ev)
        if (!d) return false

        // ISO date: yyyy-MM-dd
        if (filters.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const sel = new Date(filters.date); sel.setHours(0, 0, 0, 0)
          const evt = new Date(d); evt.setHours(0, 0, 0, 0)
          return evt.getTime() === sel.getTime()
        }

        // Date range: "yyyy-MM-dd:yyyy-MM-dd"
        if (filters.date.includes(":")) {
          const [fromStr, toStr] = filters.date.split(":")
          if (fromStr && toStr) {
            const from = new Date(fromStr); from.setHours(0, 0, 0, 0)
            const to = new Date(toStr); to.setHours(23, 59, 59, 999)
            return d >= from && d <= to
          }
          return true
        }

        if (filters.date === "Today") return d.toDateString() === now.toDateString()

        if (filters.date === "Tomorrow") {
          const tmw = new Date(now); tmw.setDate(now.getDate() + 1)
          return d.toDateString() === tmw.toDateString()
        }

        if (filters.date === "This Weekend") {
          const dayOfWeek = now.getDay()
          const saturday = new Date(now)
          if (dayOfWeek === 0) saturday.setDate(now.getDate() - 1)
          else if (dayOfWeek < 6) saturday.setDate(now.getDate() + (6 - dayOfWeek))
          saturday.setHours(0, 0, 0, 0)
          const sunday = new Date(saturday); sunday.setDate(saturday.getDate() + 1); sunday.setHours(23, 59, 59, 999)
          return d >= saturday && d <= sunday
        }

        if (filters.date === "This Week") {
          const weekLater = new Date(now); weekLater.setDate(now.getDate() + 7)
          return d >= now && d <= weekLater
        }

        if (filters.date === "This Month") {
          const start = new Date(now.getFullYear(), now.getMonth(), 1)
          const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
          return d >= start && d <= end
        }

        if (filters.date === "Next Month") {
          const start = new Date(now.getFullYear(), now.getMonth() + 1, 1)
          const end = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59, 999)
          return d >= start && d <= end
        }

        return true
      })
    }

    // ── SUBCATEGORY FILTER ────────────────────────────────────────────────────
    if (filters.subcategories?.length > 0) {
      result = result.filter((ev) =>
        ev.subcategories?.some((s) => filters.subcategories.includes(s.name))
      )
    }

    // ── FACILITATOR FILTER ────────────────────────────────────────────────────
    if (filters.facilitator) {
      const term = filters.facilitator.toLowerCase().trim()
      result = result.filter((ev) =>
        ev.owner?.full_name?.toLowerCase().includes(term) ||
        ev.hosts?.some((h) => h.full_name?.toLowerCase().includes(term))
      )
    }

    // ── FORMAT FILTER ─────────────────────────────────────────────────────────
    // Skip when both or neither selected
    if (Array.isArray(filters.format) && filters.format.length === 1) {
      const isOnline = filters.format[0] === "Online"
      result = result.filter((ev) => ev.online === isOnline)
    }

    // ── LOCATION FILTER ───────────────────────────────────────────────────────
    // Online events are never restricted by state — they're available everywhere
    if (Array.isArray(filters.location) && filters.location.length > 0) {
      const stateMap: Record<string, string> = {
        'new south wales': 'NSW', 'victoria': 'VIC', 'queensland': 'QLD',
        'south australia': 'SA', 'western australia': 'WA', 'tasmania': 'TAS',
        'northern territory': 'NT', 'australian capital territory': 'ACT',
      }
      result = result.filter((ev) => {
        if (ev.online) return true
        if (!ev.location) return false
        const loc = ev.location.toLowerCase()
        return filters.location.some((selectedState: string) => {
          const abbr = stateMap[selectedState.toLowerCase()]
          return abbr ? loc.includes(abbr.toLowerCase()) : loc.includes(selectedState.toLowerCase())
        })
      })
    }

    // ── SUBURB FILTER ─────────────────────────────────────────────────────────
    // Substring match on the event's free-form location; online events pass.
    if (typeof filters.suburb === "string" && filters.suburb.trim()) {
      const term = filters.suburb.trim().toLowerCase()
      result = result.filter((ev) => {
        if (ev.online) return true
        return ev.location?.toLowerCase().includes(term) ?? false
      })
    }

    // ── PRICE FILTER ──────────────────────────────────────────────────────────
    // Skip when all 3 are selected (default state = no restriction)
    if (filters.prices?.length > 0 && filters.prices.length < 3) {
      result = result.filter((ev) => {
        const tts = ev.ticket_types
        if (!tts?.length) return true // no ticket info — show by default
        return filters.prices.some((price: string) => {
          if (price === "Free") return tts.some((t) => t.ticket_kind === "FREE" || (t.ticket_kind === "PAID" && t.price_cents === 0))
          if (price === "Paid") return tts.some((t) => t.ticket_kind === "PAID" && t.price_cents > 0)
          if (price === "Donation") return tts.some((t) => t.ticket_kind === "DONATION")
          return false
        })
      })
    }

    // ── SORT ──────────────────────────────────────────────────────────────────
    // Prompt relevance always wins first; the Sort selection breaks ties (and
    // fully orders the list when no prompt is set).
    return result.sort((a, b) => {
      if (scoreById) {
        const sa = scoreById.get(a.id) ?? 0
        const sb = scoreById.get(b.id) ?? 0
        if (sa !== sb) return sb - sa
      }
      if (sort === "Most popular") {
        const pa = (a._count?.bookings ?? 0) + (a._count?.savedBy ?? 0)
        const pb = (b._count?.bookings ?? 0) + (b._count?.savedBy ?? 0)
        if (pa !== pb) return pb - pa
      }
      const da = getEventDate(a), db = getEventDate(b)
      if (!da && !db) return 0
      if (!da) return 1
      if (!db) return -1
      return sort === "Latest" ? db.getTime() - da.getTime() : da.getTime() - db.getTime()
    })
  }, [events, filters, savedEventIds, showOnlySaved, sort])

  // Reset page when filter results change
  useEffect(() => { setPage(0) }, [filteredAndSorted.length, filters])

  // Personalisation signal: log settled searches (query and/or filters) with
  // the result count. Debounced so typing in the smart prompt logs once, not
  // per keystroke. The returned log id lets card clicks attribute back to the
  // search that surfaced them.
  const lastSearchLogId = useRef<string | null>(null)
  useEffect(() => {
    if (showOnlySaved || adminMode) return
    const { smartPrompt, ...rest } = filters ?? {}
    const hasFilters = Object.values(rest).some((v) =>
      Array.isArray(v) ? v.length > 0 : Boolean(v)
    )
    const query = typeof smartPrompt === "string" ? smartPrompt.trim() : ""
    if (!query && !hasFilters) {
      lastSearchLogId.current = null
      return
    }
    const timer = setTimeout(() => {
      void logSearch({
        query,
        filters: hasFilters ? rest : undefined,
        resultsCount: filteredAndSorted.length,
        accessToken,
      }).then((id) => { lastSearchLogId.current = id })
    }, 1500)
    return () => clearTimeout(timer)
    // filteredAndSorted.length intentionally omitted: we log the count at the
    // moment the search settles, not on every unrelated list change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, showOnlySaved, adminMode, accessToken])

  const handleCardClick = (eventId: string) => {
    if (lastSearchLogId.current) {
      logSearchClick(lastSearchLogId.current, eventId, accessToken)
    }
  }

  // Liked/disliked badges — durable feedback from the matching game, so
  // players recognise events they've already engaged with.
  const [feedbackByEvent, setFeedbackByEvent] = useState<Record<string, 'LIKED' | 'DISLIKED'>>({})
  useEffect(() => {
    if (!accessToken || events.length === 0) return
    const ids = events.slice(0, 100).map((e) => e.id).join(',')
    fetch(`${env.NEXT_PUBLIC_API_URL}/engagement/feedback?eventIds=${ids}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((rows: { event_id: string; sentiment: 'LIKED' | 'DISLIKED' }[]) => {
        setFeedbackByEvent(Object.fromEntries(rows.map((r) => [r.event_id, r.sentiment])))
      })
      .catch(() => {})
  }, [accessToken, events])

  const totalPages = Math.ceil(filteredAndSorted.length / ITEMS_PER_PAGE)
  const displayedEvents = filteredAndSorted.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE)
  const hasEvents = filteredAndSorted.length > 0
  const origin = typeof window !== "undefined" ? window.location.origin : ""

  return (
    <div className="y-5 space-y-6 w-full">
      {!showOnlySaved && <EventsFilterBar onFilterChange={setFilters} events={events} persistFilters={!adminMode} resetSignal={filterResetSignal} />}

      {hasEvents ? (
        <>
          {/* Results head — count + sort */}
          <div className="flex items-center justify-between gap-3 pb-3.5 border-b border-[#EFE7D7]">
            <span className={`text-[14.5px] text-[#5E594F] ${FONT_BODY}`}>
              <b className={`${FONT_UI} font-bold text-[#2C2D2B]`}>{filteredAndSorted.length}</b> events near you
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={`${FONT_UI} inline-flex items-center gap-1.5 font-bold text-[13px] text-[#2C2D2B] bg-[#FFFDF8] border border-[#D9CDB9] rounded-full px-3.5 py-2 transition-colors hover:border-[#8A8475]`}
                >
                  <span className="text-[#8A8475]">Sort</span> {sort}
                  <ChevronDown className="h-3.5 w-3.5 text-[#8A8475]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuRadioGroup value={sort} onValueChange={(v) => setSort(v as SortOption)}>
                  {SORT_OPTIONS.map((o) => (
                    <DropdownMenuRadioItem key={o} value={o} className="text-[#2C2D2B]">{o}</DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Top Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 0}
                className={`flex items-center gap-2 ${PAGINATION_BUTTON}`}
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <span className={`text-xs sm:text-sm text-[#8A8475] text-center ${FONT_BODY}`}>
                Page <b className="text-[#2C2D2B]">{page + 1}</b> of {totalPages} · {filteredAndSorted.length} events
              </span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
                className={`flex items-center gap-2 ${PAGINATION_BUTTON}`}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayedEvents.map((event, index) => {
              // "Attending or potentially attending" = confirmed bookings +
              // saves, from the API's relation counts (event.attendees is a
              // legacy field the API never populates).
              const attendingCount = (event._count?.bookings ?? 0) + (event._count?.savedBy ?? 0)
              const chip = dateChipParts(event)
              const price = priceChip(event)
              const timeRange = timeRangeLabel(event)
              const eyebrow = event.subcategories?.[0]?.name ?? event.categories?.[0]?.name ?? (event.online ? "Online" : null)
              const eyebrowColor = EYEBROW_COLORS[index % EYEBROW_COLORS.length]
              const href = adminMode ? `/admin/event/${event.id}` : `/events/${event.id}`

              return (
                <article
                  key={event.id}
                  className="relative bg-[#FFFDF8] rounded-[18px] border border-[#EFE7D7] overflow-hidden transition-all hover:-translate-y-[3px] hover:shadow-[0_14px_30px_rgba(44,45,43,0.10)] flex flex-col h-full"
                >
                  <Link href={href} className="group block flex-1 flex flex-col" onClick={() => handleCardClick(event.id)} prefetch={false}>
                    {/* Cover: image + scrim + date/price chips */}
                    <div className="relative h-40 w-full overflow-hidden flex-none">
                      <Image
                        src={resolveImageUrl(
                          event.image_url?.includes(',')
                            ? event.image_url.split(',')[0]?.trim()
                            : event.image_url
                        )}
                        alt={event.title}
                        fill
                        className="object-cover transition-transform group-hover:scale-[1.02]"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        quality={85}
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-[#2C2D2B]/30 via-transparent to-[#2C2D2B]/15 pointer-events-none" />
                      {chip && (
                        <div className="absolute top-3 left-3 bg-[#FFFDF8] rounded-[11px] px-2.5 py-1.5 text-center shadow-[0_2px_8px_rgba(44,45,43,.14)]">
                          <span className={`block text-[9.5px] font-bold tracking-[.1em] uppercase text-[#DE866D] ${FONT_BODY}`}>{chip.m}</span>
                          <span className={`block text-[19px] font-bold text-[#2C2D2B] leading-none ${FONT_UI}`}>{chip.d}</span>
                        </div>
                      )}
                      {price && (
                        <span className={`absolute top-3 right-3 flex flex-col items-end gap-0.5 font-bold text-xs px-2.5 py-1.5 rounded-full bg-[#FFFDF8] text-[#2C2D2B] shadow-[0_2px_8px_rgba(44,45,43,.14)] ${FONT_UI}`}>
                          <span>{price.label}</span>
                          {price.depositNote && (
                            <span className="text-[9px] font-medium text-[#8A8475] normal-case">{price.depositNote}</span>
                          )}
                        </span>
                      )}
                      {feedbackByEvent[event.id] && (
                        <span
                          title={feedbackByEvent[event.id] === 'LIKED' ? 'You liked this event' : 'You marked this as not interested'}
                          className={`absolute bottom-3 left-3 inline-flex items-center gap-1 font-bold text-[11px] px-2 py-1 rounded-full shadow-[0_2px_8px_rgba(44,45,43,.14)] ${FONT_UI} ${
                            feedbackByEvent[event.id] === 'LIKED'
                              ? 'bg-[#FFFDF8] text-[#4E7A6F]'
                              : 'bg-[#FFFDF8] text-[#8A8475]'
                          }`}
                        >
                          {feedbackByEvent[event.id] === 'LIKED' ? '❤ Liked' : '✕ Not interested'}
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="px-4 pt-3.5 pb-0 flex-1">
                      {eyebrow && (
                        <span className={`block text-[11px] font-bold tracking-[.14em] uppercase mb-1.5 ${eyebrowColor} ${FONT_BODY}`}>{eyebrow}</span>
                      )}
                      <h3 className={`text-base font-extrabold uppercase tracking-[.02em] leading-[1.16] text-[#2C2D2B] line-clamp-2 mb-2.5 ${FONT_HEADING}`}>{event.title}</h3>
                      <div className="flex flex-col gap-0.5">
                        <span className={`inline-flex items-center gap-1 text-[13.5px] font-semibold text-[#2C2D2B] ${FONT_BODY}`}>
                          <MapPin className="h-3.5 w-3.5 text-[#75A096] flex-none" /> {summarizeLocation(event.location)}
                        </span>
                        {timeRange && <span className={`text-[13px] text-[#5E594F] ${FONT_BODY}`}>{timeRange}</span>}
                      </div>
                    </div>

                    {/* Foot: avatar stack + RSVP */}
                    <div className="flex items-center justify-between gap-2.5 mx-4 mt-3.5 mb-4 pt-3.5 border-t border-[#EFE7D7]">
                      <div className="flex items-center gap-2 min-w-0">
                        {attendingCount > 0 ? (
                          <>
                            {/* Decorative tinted circles + real count.
                                TODO(member-profiles): show real member avatars
                                once public member profiles ship. */}
                            <div className="flex -space-x-2">
                              {Array.from({ length: Math.min(3, attendingCount) }).map((_, i) => {
                                const tint = avatarGradient(event.id, i)
                                return (
                                  <span
                                    key={i}
                                    aria-hidden
                                    className="h-7 w-7 rounded-full ring-2 ring-[#FFFDF8]"
                                    style={{ background: `linear-gradient(120deg, ${tint.from}, ${tint.to})` }}
                                  />
                                )
                              })}
                            </div>
                            <span className={`text-[12.5px] font-semibold text-[#5E594F] whitespace-nowrap ${FONT_BODY}`}>
                              {attendingCount} in
                            </span>
                          </>
                        ) : (
                          <span className={`text-[12.5px] font-semibold text-[#8A8475] ${FONT_BODY}`}>Be the first in</span>
                        )}
                      </div>
                      <span
                        className={`${FONT_UI} flex-none font-bold text-[13px] rounded-full px-4 py-1.5 bg-[#F0B853] text-[#2C2D2B] shadow-[inset_0_0_0_1.4px_#D99E37] transition-all group-hover:-translate-y-px group-hover:shadow-[inset_0_0_0_1.4px_#D99E37,0_4px_12px_rgba(217,158,55,.28)]`}
                      >
                        {adminMode ? "View" : "RSVP"}
                      </span>
                    </div>
                  </Link>

                  {/* Overlay controls — siblings of the Link (not descendants),
                      so clicking them can never trigger card navigation.
                      Anchored to the cover's bottom-right, over the scrim. */}
                  {!adminMode && (
                    <div className="absolute top-[118px] right-2.5 flex gap-1.5 z-10">
                      <div className="bg-[#FFFDF8]/90 backdrop-blur-sm rounded-full shadow-sm">
                        <EventSaveButton eventId={event.id} onToggle={handleSavedToggle} save={savedEventIds.includes(event.id)} isAuthenticated={!!accessToken} accessToken={accessToken} />
                      </div>
                      <div className="bg-[#FFFDF8]/90 backdrop-blur-sm rounded-full shadow-sm">
                        <EventCalendarButton title={event.title} startTime={event.start_datetime ?? event.date} endTime={event.end_datetime ?? event.start_datetime ?? event.date} location={event.location} description={event.description} timezone={event.timezone} />
                      </div>
                      <div className="bg-[#FFFDF8]/90 backdrop-blur-sm rounded-full shadow-sm">
                        <EventShareButton eventUrl={`${origin}/events/${event.id}`} eventId={event.id} accessToken={accessToken} />
                      </div>
                    </div>
                  )}
                </article>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 0}
                className={`flex items-center gap-2 ${PAGINATION_BUTTON}`}
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <span className={`text-xs sm:text-sm text-[#5E5D59] text-center ${FONT_BODY}`}>
                Page {page + 1} of {totalPages} · {filteredAndSorted.length} events
              </span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
                className={`flex items-center gap-2 ${PAGINATION_BUTTON}`}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="bg-[#FFFDF8] border border-[#2C2D2B]/10 rounded-2xl p-10 text-center">
          <Calendar className="h-8 w-8 mx-auto text-[#75A096] mb-3" />
          <h3 className={`text-lg font-bold text-[#2C2D2B] mb-1 ${FONT_HEADING}`}>
            {emptyState === "saved" ? "No saved events" : emptyState === "filtered" ? "No events found" : "Nothing listed yet"}
          </h3>
          {emptyState === "saved" ? (
            <p className={`text-[#5E5D59] ${FONT_BODY}`}>Tap the heart on events to save them.</p>
          ) : emptyState === "filtered" ? (
            <>
              <p className={`text-[#5E5D59] ${FONT_BODY}`}>Try adjusting the filters.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                data-testid="discover-clear-filters"
                onClick={() => setFilterResetSignal((n) => n + 1)}
              >
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <p data-testid="discover-nothing-listed" className={`text-[#5E5D59] ${FONT_BODY}`}>
                <Link href="/facilitators" className="underline underline-offset-2 hover:text-[#2C2D2B]">
                  Nothing listed in your area yet — follow a facilitator and we&apos;ll let you know when they publish.
                </Link>
              </p>
              {/* #1636 — an empty server-fetched list and a genuinely empty
                  catalog render identically, with no way to tell them apart
                  or recover from the first. router.refresh() re-runs the
                  server component's fetch rather than reusing whatever the
                  client navigated in with. */}
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                data-testid="discover-refresh-empty"
                onClick={() => router.refresh()}
              >
                Refresh
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
